import { jsx as _jsx } from "react/jsx-runtime";
import React, { useState, useCallback } from "react";
import { deleteOpenList, deleteSelectedContentTypeFilters, deleteSelectedTagFilters, deleteSortFilter, } from "./utils/explorer/explorerstate";
import { removeAll as removeAllFromUploadDB } from "./utils/uploadworker/uploadworkerutil";
import { removeAll as removeAllFromZipDownloadDB } from "./utils/zipdownload/zipdownloadworkerutil";
import { getBatchId, clearBatchId } from "./utils/uploadworker/batchIdStorage";
import { getBatchStatus, cancelBatch } from "./utils/uploadworker/orchestratorClient";
export const AppContext = React.createContext({
    getState: () => {
        return {
            displayMode: "light",
            isRender: false,
            isConnection: false,
            isLoading: false,
            isLoaded: false,
            modalOpened: false,
            isErrorTicket: false,
            isWarningTicket: false,
            isInfoTicket: false,
            isDoneTicket: false,
            errorTicketText: "",
            infoTicketText: "",
            warningTicketText: "",
            doneTicketText: "",
            modalParam: {},
            isProgress: false,
            isHeaderProgress: false,
            modalName: "",
        };
    },
    isLogin: (x) => false,
    onSetEthBalance: (x) => { },
    onSetEthAddress: (x) => { },
    onSetToken: (x) => { },
    onSetErrorTicket: (x) => { },
    onSetInfoTicket: (x) => { },
    onSetWarningTicket: (x) => { },
    onSetDoneTicket: (x) => { },
    closeSetDoneTicket: (x) => { },
    onProcessed: (x) => { },
    onLogIn: () => { },
    onLogout: () => { },
    onSetModeOpen: () => { },
    onSetLightMode: () => { },
    onSetDarkMode: () => { },
    onSetDesktop: () => { },
    onSetMobile: () => { },
    onSetIsRender: () => { },
    onSetLoaderTrue: () => { },
    onSetLoaderFalse: () => { },
    openModal: (x, y, z) => { },
    closeModal: () => { },
    isProgress: (x) => false,
    isHeaderProgress: (x) => false,
    setUploadStatus: (x) => { },
    setCurrentUploadPath: (x) => { },
    setFolderProgress: (x) => { },
    updateFolderProgress: (x, y, z) => { },
    resetUploadState: () => { },
    showResumeModal: false,
    resumeBatchStatus: null,
    handleResumeContinue: () => { },
    handleResumeCancel: () => { },
    handleResumeIgnore: () => { },
});
const AppContextProvider = ({ children }) => {
    const [modalParam, setModalParam] = useState(undefined);
    const [state, setState] = useState(() => {
        const defaultState = {
            displayMode: "light",
            isRender: false,
            isLoading: false,
            isProgress: false,
            isHeaderProgress: false,
        };
        try {
            const savedState = window.localStorage.getItem("state");
            if (!savedState)
                return defaultState;
            const parsedState = JSON.parse(savedState);
            return {
                ...defaultState,
                ...parsedState,
                // Loading/progress flags describe only the current runtime. Restoring
                // them after a reload can permanently cover otherwise usable pages.
                isLoading: false,
                isProgress: false,
                isHeaderProgress: false,
            };
        }
        catch {
            window.localStorage.removeItem("state");
            return defaultState;
        }
    });
    const updateState = useCallback((newState) => {
        setState((prevState) => {
            const updatedState = { ...prevState, ...newState };
            try {
                const stateToSave = { ...updatedState };
                delete stateToSave.modalParam;
                delete stateToSave.folderProgress;
                delete stateToSave.isLoading;
                delete stateToSave.isProgress;
                delete stateToSave.isHeaderProgress;
                window.localStorage.setItem("state", JSON.stringify(stateToSave));
            }
            catch (e) {
                console.warn("localStorage quota exceeded, skipping state save");
            }
            return updatedState;
        });
    }, []);
    const getState = useCallback(() => {
        const retObj = { ...state };
        retObj.modalParam = modalParam;
        return retObj;
    }, [modalParam, state]);
    const onSetEthAddress = useCallback((ethAddress) => {
        updateState({ ethAddress });
    }, [updateState]);
    const onSetEthBalance = useCallback((ethBalance) => {
        updateState({ ethBalance });
    }, [updateState]);
    const onSetToken = useCallback((tokenName) => {
        updateState({ tokenName });
    }, [updateState]);
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [resumeBatchStatus, setResumeBatchStatus] = useState(null);
    const checkForSavedBatch = useCallback(async () => {
        const savedBatch = getBatchId();
        if (!savedBatch)
            return;
        try {
            const status = await getBatchStatus(savedBatch.batchId);
            if (status.status === "active" || status.status === "paused") {
                setResumeBatchStatus({
                    batchId: savedBatch.batchId,
                    basePath: savedBatch.basePath,
                    totalFiles: status.totalFiles,
                    completedFiles: status.completedFiles,
                    progress: status.progress,
                    status: status.status,
                });
                setShowResumeModal(true);
            }
            else {
                clearBatchId();
            }
        }
        catch (error) {
            if (error.message?.includes("404") || error.message?.includes("not found")) {
                clearBatchId();
            }
            console.error("Error checking saved batch:", error);
        }
    }, []);
    const onLogIn = useCallback(() => {
        deleteOpenList();
        deleteSortFilter();
        deleteSelectedContentTypeFilters();
        deleteSelectedTagFilters();
        updateState({ isConnected: true, isRender: !state.isRender });
        checkForSavedBatch();
    }, [state.isRender, updateState, checkForSavedBatch]);
    const onLogout = useCallback(() => {
        removeAllFromUploadDB();
        removeAllFromZipDownloadDB();
        updateState({ isConnected: false, isRender: !state.isRender });
    }, [state.isRender, updateState]);
    /*const onSetModeOpen = useCallback(() => {
      updateState({ modeOpen: !state.modeOpen });
    }, [state.modeOpen, updateState]);*/
    const onSetModeOpen = useCallback(() => {
        updateState({ modeOpen: true });
    }, [updateState]);
    const onSetLightMode = useCallback(() => {
        updateState({ isRender: !state.isRender });
        //document.documentElement.setAttribute("data-color-mode", "light");
    }, [state.isRender, updateState]);
    const onSetDarkMode = useCallback(() => {
        updateState({ isRender: !state.isRender });
        document.documentElement.setAttribute("data-color-mode", "dark");
    }, [state.isRender, updateState]);
    const onSetDesktop = useCallback(() => {
        updateState({ isRender: !state.isRender });
        document.documentElement.setAttribute("data-size-mode", "desktop");
    }, [state.isRender, updateState]);
    const onSetMobile = useCallback(() => {
        updateState({ isRender: !state.isRender });
        document.documentElement.setAttribute("data-size-mode", "mobile");
    }, [state.isRender, updateState]);
    const isLogin = useCallback((isConnection) => {
        updateState({ isConnection: isConnection, isRender: !state.isRender });
    }, [state.isRender, updateState]);
    const isProgress = useCallback((isProgress) => {
        updateState({ isProgress: isProgress });
    }, [updateState]);
    const isHeaderProgress = useCallback((isHeaderProgress) => {
        updateState({ isHeaderProgress: isHeaderProgress });
    }, [updateState]);
    const onSetIsRender = useCallback(() => {
        updateState({ isRender: !state.isRender });
    }, [state.isRender, updateState]);
    const onProcessed = useCallback((text) => {
        updateState({ isLoaded: text });
    }, [updateState]);
    const onSetLoaderTrue = useCallback(() => {
        updateState({ isLoading: true });
    }, [updateState]);
    const onSetLoaderFalse = useCallback(() => {
        updateState({ isLoading: false });
    }, [updateState]);
    const openModal = useCallback((modalName, modalText, param) => {
        updateState({ modalOpened: true, modalName: modalName, modalText: modalText });
        setModalParam(param);
    }, [updateState]);
    const closeModal = useCallback(() => {
        updateState({ modalOpened: false, modalName: "", modalText: "" });
    }, [updateState]);
    const onSetErrorTicket = useCallback((text) => {
        updateState({ isErrorTicket: !state.isErrorTicket, errorTicketText: text });
    }, [state.isErrorTicket, updateState]);
    const onSetInfoTicket = useCallback((text) => {
        updateState({ isInfoTicket: !state.isInfoTicket, infoTicketText: text });
    }, [state.isInfoTicket, updateState]);
    const onSetWarningTicket = useCallback((text) => {
        updateState({ isWarningTicket: !state.isWarningTicket, warningTicketText: text });
    }, [state.isWarningTicket, updateState]);
    const onSetDoneTicket = useCallback((text) => {
        updateState({ isDoneTicket: true, doneTicketText: text });
    }, [updateState]);
    const closeSetDoneTicket = useCallback((text) => {
        updateState({ isDoneTicket: false, doneTicketText: text });
    }, [updateState]);
    /**
     * Upload állapot kezelő függvények - Követelmény 2.3/2.4
     * Ezek a függvények az upload folyamat állapotát kezelik
     */
    const [uploadStatus, setUploadStatusState] = useState("idle");
    const [currentUploadPath, setCurrentUploadPathState] = useState("");
    const [folderProgress, setFolderProgressState] = useState([]);
    const setUploadStatus = useCallback((status) => {
        setUploadStatusState(status);
    }, []);
    const setCurrentUploadPath = useCallback((path) => {
        setCurrentUploadPathState(path);
    }, []);
    const setFolderProgress = useCallback((progress) => {
        setFolderProgressState(progress);
    }, []);
    const updateFolderProgress = useCallback((folderPath, completedFiles, totalFiles) => {
        setFolderProgressState((prev) => {
            const existing = prev.find((f) => f.path === folderPath);
            const progress = totalFiles > 0 ? (completedFiles / totalFiles) * 100 : 0;
            const completed = completedFiles >= totalFiles;
            if (existing) {
                return prev.map((f) => f.path === folderPath ? { ...f, completedFiles, totalFiles, progress, completed } : f);
            }
            else {
                return [...prev, { path: folderPath, totalFiles, completedFiles, progress, completed }];
            }
        });
    }, []);
    const resetUploadState = useCallback(() => {
        setUploadStatusState("idle");
        setCurrentUploadPathState("");
        setFolderProgressState([]);
    }, []);
    const handleResumeContinue = useCallback(() => {
        if (resumeBatchStatus) {
            setShowResumeModal(false);
            openModal("fileupload", "", {
                resumeBatchId: resumeBatchStatus.batchId,
                resumeBasePath: resumeBatchStatus.basePath,
                resumeCompletedFiles: resumeBatchStatus.completedFiles,
                resumeTotalFiles: resumeBatchStatus.totalFiles
            });
        }
    }, [resumeBatchStatus, openModal]);
    const handleResumeCancel = useCallback(async () => {
        if (resumeBatchStatus) {
            try {
                await cancelBatch(resumeBatchStatus.batchId);
            }
            catch (error) {
                console.error("Error canceling batch:", error);
            }
            clearBatchId();
            setResumeBatchStatus(null);
            setShowResumeModal(false);
        }
    }, [resumeBatchStatus]);
    const handleResumeIgnore = useCallback(() => {
        setShowResumeModal(false);
    }, []);
    const getStateWithUpload = useCallback(() => {
        const baseState = getState();
        return {
            ...baseState,
            uploadStatus,
            currentUploadPath,
            folderProgress,
        };
    }, [getState, uploadStatus, currentUploadPath, folderProgress]);
    return (_jsx(AppContext.Provider, { value: {
            getState: getStateWithUpload,
            onSetEthBalance,
            onSetEthAddress,
            onSetToken,
            onLogout,
            onLogIn,
            onSetModeOpen,
            onSetLightMode,
            onSetDarkMode,
            onSetDesktop,
            onSetMobile,
            isLogin,
            onSetIsRender,
            onSetLoaderTrue,
            onSetLoaderFalse,
            openModal,
            closeModal,
            onSetErrorTicket,
            onSetInfoTicket,
            onSetWarningTicket,
            onSetDoneTicket,
            closeSetDoneTicket,
            onProcessed,
            isProgress,
            isHeaderProgress,
            setUploadStatus,
            setCurrentUploadPath,
            setFolderProgress,
            updateFolderProgress,
            resetUploadState,
            showResumeModal,
            resumeBatchStatus,
            handleResumeContinue,
            handleResumeCancel,
            handleResumeIgnore,
        }, children: children }));
};
export default AppContextProvider;
